/**
 * Toast renderer — mounts at app root and subscribes to the toast event bus.
 *
 * The imperative API lives in `@/components/ui/toast` (separate zero-dep module)
 * so lazy-loaded chunks importing `toast.success(…)` don't create a shared-chunk
 * bridge that inflates the root bundle.
 */
import { useEffect, useState } from 'react';
import { subscribe, getItems, type ToastItem } from '@/components/ui/toast';
export { toast } from '@/components/ui/toast';

const dismiss = (id: number) => {
  // Re-use the bus dismiss so it notifies all subscribers
  import('@/components/ui/toast').then(m => m.toast.dismiss(id));
};

export function ToastProvider(): JSX.Element {
  const [current, setCurrent] = useState<ToastItem[]>(getItems);

  useEffect(() => subscribe(setCurrent), []);

  if (current.length === 0) return <></>;

  return (
    <div
      aria-live="polite"
      aria-atomic="true"
      className="fixed top-4 right-4 z-[9998] flex flex-col gap-2 pointer-events-none"
      style={{ maxWidth: 'calc(100vw - 2rem)' }}
    >
      {current.map((t) => (
        <ToastCard key={t.id} item={t} />
      ))}
    </div>
  );
}

function ToastCard({ item }: { item: ToastItem }): JSX.Element {
  const palette = {
    success: { bg: 'bg-emerald-600', icon: '✓', border: 'border-emerald-700' },
    error:   { bg: 'bg-red-600',     icon: '✕', border: 'border-red-700' },
    info:    { bg: 'bg-sky-600',     icon: 'ℹ', border: 'border-sky-700' },
    warning: { bg: 'bg-amber-500',   icon: '!', border: 'border-amber-600' },
  }[item.type];

  return (
    <div
      role="status"
      className={`pointer-events-auto ${palette.bg} ${palette.border} text-white px-4 py-3 rounded-lg shadow-lg flex items-start gap-3 min-w-[260px] max-w-sm animate-slideIn`}
    >
      <span className="text-lg font-bold leading-none mt-0.5">{palette.icon}</span>
      <p className="text-sm flex-1 leading-snug">{item.message}</p>
      <button
        onClick={() => dismiss(item.id)}
        className="text-white/70 hover:text-white text-sm leading-none"
        aria-label="Dismiss notification"
      >
        ✕
      </button>
    </div>
  );
}
